import { DRAWING_CURSORS } from './drawingCursor.js'
import { drawingColor } from './drawingColor.js'
import { installFreehandInteraction } from './freehandInteraction.js'
import { coordinateToChartTime } from '../../../lib/chart/chartTimeProjection.js'
import { snapTrendlinePoint } from './trendlineSnap.js'
import { installDrawingDrag } from './drawingDrag.js'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { isEditableTarget, shouldShieldKeyboardEvent } from '../../../commands/commandModel.js'
import { useServerDrawingRecord } from './annotationStore.js'
import DrawingSettings from './DrawingSettings.jsx'
import { AnnotationPrimitive, normalizeAnnotations } from './AnnotationPrimitive.js'

const normalizePenStyle = value => [{ width: Math.max(1,Math.min(4,Number(value?.[0]?.width)||2)), color: /^#[0-9a-f]{6}$/i.test(value?.[0]?.color || '') ? value[0].color : '#2962ff', smoothing: Number.isFinite(value?.[0]?.smoothing) ? Math.max(0,Math.min(100,value[0].smoothing)) : 50 }]

export default forwardRef(function ChartDrawings({ api, userId, storageKey, timeframe, drawingView = 'shared', theme = 'light', tool, active, keyboardActive = true, onToolChange, onActivate }, ref) {
  const { items, setItems, status, retry } = useServerDrawingRecord(userId, storageKey, normalizeAnnotations, drawingView)
  const ready = !['loading', 'load-error'].includes(status)
  const penDefaults = useServerDrawingRecord(userId, '__pen-defaults-v1', normalizePenStyle)
  const penStyle = penDefaults.items[0] || { width: 2, color: '#2962ff', smoothing: 50 }
  const [defaultsOpen, setDefaultsOpen] = useState(false)
  const [notice, setNotice] = useState('')
  const [settingsId, setSettingsId] = useState(null)
  const [dragPreview, setDragPreview] = useState(null)
  const [opacityDraft, setOpacityDraft] = useState(null)
  const renderedItems = useMemo(() => items.map(item => opacityDraft?.ids.includes(item.id) ? { ...item, opacity: opacityDraft.value } : dragPreview?.id === item.id ? dragPreview : item), [items, dragPreview, opacityDraft])
  const [selected, setSelected] = useState(null)
  const [draft, setDraft] = useState(null)
  const [color, setColor] = useState('#2962ff')
  const [text, setText] = useState('')
  const settingsSession = useRef(null)
  const freehand = useRef(null)
  const openSettings = value => { const ids = Array.isArray(value) ? value : [value]; settingsSession.current = { ids, recorded: false }; setSettingsId(ids[0]) }
  const saveSettings = (value, changes) => {
    const normalized = normalizeAnnotations([value])[0]
    if (!ready || !normalized || JSON.stringify(items.find(item => item.id === value.id)) === JSON.stringify(normalized)) return
    if (!settingsSession.current?.recorded) {
      past.current = [...past.current.slice(-49), items]; future.current = []
      settingsSession.current = { ...settingsSession.current, recorded: true }
    }
    const ids = settingsSession.current?.ids || [value.id]
    const original = items.find(item => item.id === value.id)
    const patch = Object.fromEntries(Object.entries(changes || normalized).filter(([key,val]) => !['id','kind','points'].includes(key) && JSON.stringify(val) !== JSON.stringify(original?.[key])))
    setItems(items.map(item => ids.includes(item.id) ? (item.id === value.id ? normalized : { ...item, ...patch }) : item))
  }
  const past = useRef([]), future = useRef([]), primitive = useRef(null), current = useRef(null)
  const commit = next => {
    if (!ready) return
    past.current = [...past.current.slice(-49), items]; future.current = []
    setItems(next); setDraft(null)
  }
  const cancel = () => { setDraft(null); onToolChange('select') }
  const selectedIds = Array.isArray(selected) ? selected : selected ? [selected] : []
  const remove = () => { if (selectedIds.length) { commit(items.filter(item => !selectedIds.includes(item.id))); setSelected(null); setSettingsId(null) } }
  const undo = () => { if (draft) { setDraft(null); onToolChange('select'); return } if (past.current.length) { future.current.push(items); setItems(past.current.pop()); setSelected(null); setDraft(null) } }
  const redo = () => { if (future.current.length) { past.current.push(items); setItems(future.current.pop()); setSelected(null); setDraft(null) } }
  const onTimeframe = item => !item.timeframes?.length || item.timeframes.includes(timeframe)
  const toggleVisibility = ids => {
    const targets = items.filter(item => ids.includes(item.id) && onTimeframe(item))
    if (!targets.length) return
    const visible = !targets.some(item => item.visible !== false)
    commit(items.map(item => targets.includes(item) ? { ...item, visible } : item))
  }
  const finishOpacity = save => {
    if (save && opacityDraft) commit(items.map(item => opacityDraft.ids.includes(item.id) ? { ...item, opacity: opacityDraft.value } : item))
    setOpacityDraft(null)
  }
  useImperativeHandle(ref, () => ({ undo, redo, remove, cancel,
    hideSelected: () => toggleVisibility(selectedIds),
    hideAll: () => toggleVisibility(items.filter(onTimeframe).map(item => item.id)),
    opacity: () => { const targets=items.filter(item=>selectedIds.includes(item.id) && onTimeframe(item)); if(ready && targets.length) setOpacityDraft({ids:targets.map(item=>item.id),value:targets[0].opacity ?? 100}) },
    clearAll: () => { freehand.current?.cancel?.(); if (items.length) commit([]); setSelected(null); setSettingsId(null); setDraft(null) },
    selectAll: () => { onToolChange('select'); setSelected(items.map(item => item.id)) },
    settings: () => { if (selectedIds.length) openSettings(selectedIds) },
    penSettings: () => { if (selectedIds.length) openSettings(selectedIds); else setDefaultsOpen(true) },
  }), [items, selected, draft, ready, timeframe])
  useEffect(() => { freehand.current?.cancel?.(); setDraft(null); if (tool !== 'select') setSelected(null) }, [tool])
  useEffect(() => { freehand.current?.cancel?.(); setOpacityDraft(null); setDraft(null); setSelected(null); setSettingsId(null); setDragPreview(null); settingsSession.current = null }, [timeframe])
  useEffect(() => { if (!active) { freehand.current?.cancel?.(); setOpacityDraft(null); setDraft(null) } }, [active])
  current.current = { items, selected, draft, tool, active, color, text, penStyle, ready: ready && !['loading','load-error'].includes(penDefaults.status), onLimit: () => setNotice('This chart has 200 drawings. Erase a drawing before adding another.'), commit, onActivate, onToolChange }
  useEffect(() => {
    if (!api) return undefined
    const overlay = new AnnotationPrimitive(timeframe); primitive.current = overlay
    api.series.attachPrimitive(overlay)
    let shiftHeld = false, lastMove = null
    const pointAt = event => {
      if (!event.point || (event.paneIndex ?? 0) !== (api.paneIndex ?? 0)) return null
      const time = event.time ?? coordinateToChartTime(api.chart, api.series, event.point.x, timeframe)
      if (!Number.isFinite(time)) return null
      const state = current.current
      const anchor = state.draft?.editing ? state.draft.item.points[1 - state.draft.endpoint] : state.draft?.points[0]
      const kind = state.draft?.editing ? state.draft.item.kind : state.draft?.kind
      if (anchor && kind === 'line' && (event.shiftKey ?? event.sourceEvent?.shiftKey ?? shiftHeld)) return snapTrendlinePoint({ ...api, timeframe }, anchor, event.point)
      const price = api.series.coordinateToPrice(event.point.y)
      return Number.isFinite(price) ? { time, price } : null
    }
    const click = event => {
      const state = current.current
      if (['pen','eraser','avwap'].includes(state.tool)) return
      const point = pointAt(event)
      if (!point || !state.ready) return
      state.onActivate()
      if (state.tool === 'select' && !state.draft?.editing) {
        const id = overlay.pick(event.point.x, event.point.y)
        setSelected(id)
        const item = state.items.find(item => item.id === id)
        if (item) { setColor(item.color); setText(item.text) }
        return
      }
      if (state.draft?.editing) {
        const replacement = { ...state.draft.item, points: [...state.draft.item.points] }
        replacement.points[state.draft.endpoint] = point
        state.commit(state.items.map(item => item.id === replacement.id ? replacement : item))
        return
      }
      if (state.draft) {
        const item = { ...state.draft, points: [state.draft.points[0], point] }
        state.commit([...state.items, item]); state.onToolChange('select'); setSelected(item.id)
      } else {
        if (state.items.length >= 200) { state.onLimit(); return }
        const item = { timeframes: [timeframe], id: crypto.randomUUID(), kind: state.tool, points: [point], color: state.color,
          text: state.text || (state.tool === 'text' ? 'Label' : ''), ...(state.tool === 'rectangle' ? { fillColor: state.color, fillOpacity: 15, borderVisible: true, fillVisible: true, textVertical: 'middle' } : {}), dashed: state.tool === 'horizontal' }
        if (['line', 'circle', 'rectangle'].includes(state.tool)) setDraft({ ...item, points: [point, point] })
        else if (['text', 'horizontal'].includes(state.tool)) {
          state.commit([...state.items, item]); state.onToolChange('select'); setSelected(item.id); setText(item.text)
        }
      }
    }
    const move = event => {
      lastMove = event
      const state = current.current
      if (!state.draft) return
      const point = pointAt(event)
      if (!point) return
      if (state.draft.editing) return
      setDraft({ ...state.draft, points: [state.draft.points[0], point] })
    }
    // LWC intentionally suppresses rapid, distant second clicks. Drawing
    // placement uses pointer releases on the public price-pane element so every
    // endpoint is accepted; native selection/crosshair/rendering stay intact.
    const pane = api.chart.panes?.()[api.paneIndex ?? 0]
    const element = pane?.getHTMLElement()
    let down = null, suppressNativeUntil = 0
    const pointerDown = event => { if (event.button === 0) down = { x: event.clientX, y: event.clientY, id: event.pointerId } }
    const pointerUp = event => {
      const state = current.current
      if (['pen','eraser'].includes(state.tool)) { down = null; return }
      if (!down || down.id !== event.pointerId) return
      const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y)
      down = null
      if (moved > 5 || (state.tool === 'select' && !state.draft?.editing)) return
      const bounds = element.getBoundingClientRect()
      const x = event.clientX - bounds.left - api.chart.priceScale('left').width()
      const y = event.clientY - bounds.top
      const scale = api.chart.timeScale()
      if (x < 0 || x > scale.width() || y < 0 || y > pane.getHeight()) return
      suppressNativeUntil = Date.now() + 100
      click({ point: { x, y }, time: scale.coordinateToTime(x), paneIndex: api.paneIndex ?? 0, shiftKey: event.shiftKey })
    }
    const nativeClick = event => {
      if (Date.now() < suppressNativeUntil) return
      if (!element || (current.current.tool === 'select' && !current.current.draft?.editing)) click(event)
    }
    const shiftKey = event => {
      if (event.key !== 'Shift') return
      shiftHeld = event.type === 'keydown'
      if (lastMove && current.current.draft) move({ ...lastMove, shiftKey: shiftHeld })
    }
    const blur = () => { shiftHeld = false }
    window.addEventListener('keydown', shiftKey)
    window.addEventListener('keyup', shiftKey)
    window.addEventListener('blur', blur)
    element?.addEventListener('pointerdown', pointerDown)
    element?.addEventListener('pointerup', pointerUp)
    const doubleClick = event => {
      if (Date.now() < suppressNativeUntil) return
      const state = current.current
      if (!state.ready || !event.point || (event.paneIndex ?? 0) !== (api.paneIndex ?? 0)) return
      if (state.tool !== 'select' || state.draft) { click(event); return }
      const id = overlay.pick(event.point.x, event.point.y)
      if (id) { setSelected(id); openSettings(id); state.onActivate(); state.onToolChange('select') }
    }
    const stopFreehand = installFreehandInteraction({ api, overlay, timeframe, getState: () => current.current,
      onSelect: setSelected, onSettings: openSettings, suppressClicks: () => { suppressNativeUntil = Date.now() + 150 } })
    freehand.current = stopFreehand
    const stopDrag = installDrawingDrag({ api: { ...api, timeframe }, overlay, getState: () => current.current, onSelect: setSelected, onPreview: setDragPreview,
      onSettings: openSettings, suppressClicks: () => { suppressNativeUntil = Date.now() + 100 } })
    api.chart.subscribeDblClick(doubleClick)
    api.chart.subscribeClick(nativeClick); api.chart.subscribeCrosshairMove(move)
    return () => {
      freehand.current = null; stopFreehand(); stopDrag()
      window.removeEventListener('keydown', shiftKey)
      window.removeEventListener('keyup', shiftKey)
      window.removeEventListener('blur', blur)
      primitive.current = null
      element?.removeEventListener('pointerdown', pointerDown)
      element?.removeEventListener('pointerup', pointerUp)
      try { api.chart.unsubscribeDblClick(doubleClick); api.chart.unsubscribeClick(nativeClick); api.chart.unsubscribeCrosshairMove(move); api.series.detachPrimitive(overlay) } catch { overlay.detached() }
    }
  }, [api, timeframe])
  useEffect(() => {
    const element = api?.chart.panes?.()[api.paneIndex ?? 0]?.getHTMLElement()
    const cursor = DRAWING_CURSORS[tool]
    if (!element || !cursor) return undefined
    const previous = element.style.cursor
    element.style.cursor = cursor
    return () => { element.style.cursor = previous }
  }, [api, timeframe, tool])
  useEffect(() => { primitive.current?.setTheme(theme) }, [api, timeframe, theme])
  useEffect(() => {
    primitive.current?.update([...renderedItems.filter(item => item.visible !== false && (!item.timeframes?.length || item.timeframes.includes(timeframe))), ...(draft && !draft.editing ? [draft] : [])], selected)
  }, [api, renderedItems, draft, selected, timeframe])
  useEffect(() => {
    if (!api) return undefined
    const levels = renderedItems.filter(item => item.kind === 'horizontal' && item.visible !== false && (!item.timeframes?.length || item.timeframes.includes(timeframe))).map(item => api.series.createPriceLine({
      price: item.points[0].price, color: item.opacity == null ? drawingColor(item.color,theme) : `${drawingColor(item.color,theme)}${Math.round(item.opacity * 2.55).toString(16).padStart(2, '0')}`, lineWidth: item.width || 2, lineStyle: item.dashed ? 1 : 0, axisLabelVisible: false, title: '',
    }))
    return () => levels.forEach(line => { try { api.series.removePriceLine(line) } catch { /* disposed */ } })
  }, [api, renderedItems, timeframe, theme])
  useEffect(() => {
    if (!api) return undefined
    if (api.manageInteraction === false) return undefined
    const previous = structuredClone({ handleScroll: api.chart.options().handleScroll, handleScale: api.chart.options().handleScale })
    const drawing = !['select','pen','eraser'].includes(tool) || Boolean(draft)
    if (drawing) api.chart.applyOptions({ handleScroll: false })
    return () => { if (drawing) try { api.chart.applyOptions({ handleScroll: previous.handleScroll, handleScale: previous.handleScale }) } catch { /* disposed */ } }
  }, [api, tool, Boolean(draft)])
  useEffect(() => {
    if (!active || !keyboardActive) return undefined
    const onKey = event => {
      if (shouldShieldKeyboardEvent(event) || isEditableTarget(document.activeElement)) return
      if (['Delete', 'Backspace'].includes(event.key) && selectedIds.length > 0) { event.preventDefault(); remove(); return }
      if (event.key !== 'Escape') return
      if (tool !== 'select' || draft || selected) { event.preventDefault(); cancel(); setSelected(null) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, keyboardActive, tool, draft, selected])
  if (!active) return null
  return <>
    {opacityDraft && <div role="dialog" aria-label="Drawing opacity" className="absolute left-1/2 z-40 -translate-x-1/2 rounded border border-border bg-canvas px-3 py-2 text-xs text-ink shadow-lg"
      style={{ top: Math.max(8, (api?.chart.panes?.()[api.paneIndex ?? 0]?.getHeight?.() || 200) - 72) }}
      onKeyDown={event=>{ event.stopPropagation(); if(event.key==='Enter'){event.preventDefault();finishOpacity(true)} if(event.key==='Escape'){event.preventDefault();finishOpacity(false)} }}>
      <label className="flex items-center gap-2">Opacity <input autoFocus aria-label="Drawing opacity" type="range" min="0" max="100" step="1" value={opacityDraft.value}
        onChange={event=>setOpacityDraft(current=>({...current,value:Number(event.target.value)}))}
        onKeyDown={event=>{if(['ArrowUp','ArrowDown'].includes(event.key)){event.preventDefault();setOpacityDraft(current=>({...current,value:Math.max(0,Math.min(100,current.value+(event.key==='ArrowUp'?1:-1)))}))}}}/><output>{opacityDraft.value}%</output></label>
      <div className="mt-1 flex justify-end gap-3"><button type="button" onClick={()=>finishOpacity(false)}>Cancel</button><button type="button" onClick={()=>finishOpacity(true)}>Save</button></div>
    </div>}
    {notice && <button type="button" role="status" className="absolute bottom-8 left-2 z-30 rounded bg-canvas px-2 py-1 text-xs text-inkMuted" onClick={() => setNotice('')}>{notice}</button>}
    {defaultsOpen && <DrawingSettings key="pen-defaults" userId={userId} defaultMode item={{id:'defaults',kind:'pen',points:[{time:0,price:0}],text:'',...penStyle}}
      onSave={value => penDefaults.setItems([{width:value.width,color:value.color,smoothing:value.smoothing}])} onClose={() => setDefaultsOpen(false)} />}
    {penDefaults.status.endsWith('error') && <button type="button" role="status" onClick={penDefaults.retry} className="absolute bottom-14 left-2 z-30 bg-canvas text-xs">Pen defaults could not sync. Retry</button>}
    {status.endsWith('error') && <div role="status" className="absolute bottom-8 left-2 z-30 rounded bg-canvas px-2 py-1 text-xs text-inkMuted">
      {status === 'loading' ? 'Loading drawings…' : status === 'saving' ? 'Saving drawings…' : status === 'load-error' ? 'Drawings unavailable.' : 'Drawings not saved.'}
      {status.endsWith('error') && <button type="button" className="ml-2 underline" onClick={retry}>Retry drawings</button>}
    </div>}
    {settingsId && items.find(row => row.id === settingsId) && <DrawingSettings key={settingsId} userId={userId} api={api}
      groupCount={settingsSession.current?.ids?.length || 1} item={items.find(row => row.id === settingsId)} onClose={() => setSettingsId(null)}
      onSave={saveSettings} />}

  </>
})
